import React, { useEffect, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  InputNumber,
  Button,
  Space,
  Spin,
  DatePicker,
  App,
} from 'antd';
import { ArrowLeftOutlined, LineChartOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { RelatedMocsPanel } from '@/components/moc/RelatedMocsPanel';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { projectKeys, useProject } from '@/hooks/useProjects';
import { projectService } from '@/services/project.service';
import customerService from '@/services/customer.service';
import { userService } from '@/services/user.service';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const LIST_PATH = '/prj/projects';

export const ProjectFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = Boolean(id);

  const { data: project, isLoading: loadingProject } = useProject(id ?? '');

  const { data: customers = [], isLoading: loadingCustomers } = useQuery({
    queryKey: ['customers', 'active', 'project-form'],
    queryFn: () => customerService.getActiveCustomers(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['users', 'active', 'project-form'],
    queryFn: () => userService.getAll({ is_active: true }),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isEdit || !project) return;
    const start = project.start_date ? dayjs(project.start_date) : undefined;
    const end = project.planned_end_date ? dayjs(project.planned_end_date) : undefined;
    form.setFieldsValue({
      project_name: project.project_name,
      customer_id: project.customer_id,
      project_manager: project.project_manager || undefined,
      department_id: project.department_id || undefined,
      contract_value: Number(project.contract_value ?? 0),
      dateRange: start && end ? [start, end] : start ? [start, start] : undefined,
      project_status: project.project_status || 'Planning',
      description: project.description,
    });
  }, [isEdit, project, form]);

  const onFinish = async () => {
    try {
      const values = await form.validateFields();
      const customerName =
        customers.find((c) => c.id === values.customer_id)?.customer_name ?? '';
      const payload = {
        project_name: values.project_name,
        customer_id: values.customer_id,
        customer_name: customerName,
        contract_value: values.contract_value,
        start_date: values.dateRange?.[0]?.format('YYYY-MM-DD'),
        planned_end_date: values.dateRange?.[1]?.format('YYYY-MM-DD'),
        project_status: values.project_status,
        project_manager: values.project_manager,
        department_id: values.department_id,
        description: values.description,
      };
      setSubmitting(true);
      if (isEdit && id) {
        await projectService.updateProject(id, payload);
        message.success('Project updated');
      } else {
        await projectService.createProject(payload);
        message.success('Project created');
      }
      await queryClient.invalidateQueries({ queryKey: projectKeys.all });
      navigate(LIST_PATH);
    } catch (e: unknown) {
      if ((e as any)?.errorFields) return;
      message.error((e as Error)?.message || 'Could not save project');
    } finally {
      setSubmitting(false);
    }
  };

  const pageLoading = isEdit && loadingProject;
  const formDisabled = loadingCustomers || loadingUsers;

  return (
    <div>
      {id ? <AiPageContextSync module="projects" docType="Project" docId={id} /> : null}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Space>
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(LIST_PATH)} style={{ paddingLeft: 0 }}>
              Back to projects
            </Button>
            {isEdit && id ? (
              <Button type="link" icon={<LineChartOutlined />} onClick={() => navigate(`/prj/projects/${id}/status-check`)}>
                Project health check
              </Button>
            ) : null}
          </Space>
          <Title level={2} style={{ marginTop: 8, marginBottom: 4 }}>
            {isEdit ? 'Edit project' : 'Create project'}
          </Title>
          <Text type="secondary">
            {isEdit ? 'Update project details and timeline.' : 'Define customer, contract, and timeline.'}
          </Text>
        </div>

        <Card>
          {pageLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ project_status: 'Planning' }}
              disabled={formDisabled}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="project_name"
                    label="Project name"
                    rules={[{ required: true, message: 'Please enter project name' }]}
                  >
                    <Input placeholder="Project name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="customer_id"
                    label="Customer"
                    rules={[{ required: true, message: 'Please select customer' }]}
                  >
                    <Select
                      placeholder="Select customer"
                      showSearch
                      optionFilterProp="children"
                      allowClear
                      loading={loadingCustomers}
                    >
                      {customers.map((c) => (
                        <Select.Option key={c.id} value={c.id}>
                          {c.customer_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="project_manager" label="Project manager">
                    <Select
                      placeholder="Select project manager"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      loading={loadingUsers}
                    >
                      {users.map((u) => (
                        <Select.Option key={u.id} value={u.id}>
                          {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.username}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="department_id"
                    label="Department"
                    rules={[{ required: true, message: 'Department is required' }]}
                  >
                    <DepartmentSelect />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="contract_value"
                    label="Contract value (AED)"
                    rules={[{ required: true, message: 'Please enter contract value' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="dateRange"
                    label="Project timeline"
                    rules={[{ required: true, message: 'Please select dates' }]}
                  >
                    <RangePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="project_status" label="Status">
                    <Select>
                      <Select.Option value="Planning">Planning</Select.Option>
                      <Select.Option value="In Progress">In Progress</Select.Option>
                      <Select.Option value="On Hold">On Hold</Select.Option>
                      <Select.Option value="Completed">Completed</Select.Option>
                      <Select.Option value="Cancelled">Cancelled</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="description" label="Description">
                <TextArea rows={4} placeholder="Project description..." />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={submitting}
                  >
                    Save
                  </Button>
                  {isEdit && id && (
                    <Button
                      icon={<LineChartOutlined />}
                      onClick={() => navigate(`/prj/projects/${id}/budgets`)}
                    >
                      Cost Budget
                    </Button>
                  )}
                  <Button onClick={() => navigate(LIST_PATH)}>Cancel</Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Card>
        {isEdit && id ? <RelatedMocsPanel projectId={id} /> : null}
      </Space>
    </div>
  );
};
